import { useCallback, useEffect, useState } from "react";
import { Table, Pagination, TextFilter } from "@awsui/components-react";
import {
  TableHeader,
  TableEmptyState,
  Preferences,
  DeleteModal,
} from "./ListCampaignsComponents";
import { Link } from "react-router-dom";
import { listCampaigns } from "../../common/api";

import { ContentContainer, StatusText } from "../shared/Components";
import {
  CampaignStatus,
  Campaign,
  ListJobsTableRecord,
} from "../../appstore/models";
import { deleteJobs } from "./listJobsHelpers";
import styled from "styled-components";
import { useStoreState } from "../../appstore";
import * as Humanize from "humanize-plus";

const JobLink = styled(Link)`
  color: ${(props) => props.theme.linkBlue};
  text-decoration: none;
  display: inline;
  &:hover {
    text-decoration: underline;
  }
`;

export const LIST_JOBS_COLUMN_DEFINITIONS: ListJobsTableRecord[] = [
  {
    id: "JobName",
    header: "Name",
    cell: (item) => (
      <div>
        <JobLink to={`/geo-campaigns/${item.JobName}`}>{item.JobName}</JobLink>
      </div>
    ),
    minWidth: 300,
    allowLineWrap: true,
  },
  {
    id: "Status",
    header: "Status",
    cell: (item) => {
      const itemStatus = item.Status as CampaignStatus;
      const status =
        itemStatus === "Created"
          ? "success"
          : itemStatus === "Error"
          ? "error"
          : "in_progress";
      return <StatusText status={status}>{item.Status}</StatusText>;
    },

    minWidth: 100,
    allowLineWrap: true,
  },
  {
    id: "ImageCount",
    header: "Image Count",
    cell: (item) => {
      return (
        <span title={Humanize.intComma(item.ImageCount)}>
          {Humanize.compactInteger(item.ImageCount)}
        </span>
      );
    },

    minWidth: 100,
    allowLineWrap: true,
  },
  {
    id: "TotalChipCount",
    header: "Chip Count",
    cell: (item) => {
      return (
        <span title={Humanize.intComma(item.TotalChipCount)}>
          {Humanize.compactInteger(item.TotalChipCount)}
        </span>
      );
    },

    minWidth: 100,
    allowLineWrap: true,
  },
  {
    id: "DateCreated",
    header: "DateCreated",
    cell: (item) => item.DateCreated,
    minWidth: 100,
    allowLineWrap: true,
  },
];

const ListCampaigns = () => {
  const isAuthenticated = useStoreState((s) => s.internal.auth.isAuthenticated);
  /*
  const geoLabelingJobConfigurationSummaryIds = useStoreState(
    (s) => s.campaigns.JobNames
  );
  */
  const [searchTriggered, setSearchTriggered] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [pageCount, setPageCount] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Campaign[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(1);
  const [preferences, setPreferences] = useState({
    pageSize: 10,
    visibleContent: [
      "JobName",
      "Status",
      "ImageCount",
      "TotalChipCount",
      "DateCreated",
    ],
    wrapLines: false,
  });
  const [nextToken, setNextToken] = useState<AWS.DynamoDB.Key | undefined>(
    undefined
  );
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  /*
  const calculatePageCount = useCallback(
    (totalJobs: number) => {
      return Math.ceil(totalJobs / preferences.pageSize);
    },
    [preferences.pageSize]
  );
  const jobSearchParameters = useMemo(() => {
    const request: ListJobsRequest = {
      PageNumber: currentPageIndex,
      MaxResults: preferences.pageSize,
      Filter: {},
      NextToken: nextToken,
    }; // Add filter options
    if (searchQuery.trim() !== "") {
      request.Filter.JobName = searchQuery.trim();
    }
    return request;
  }, [currentPageIndex, nextToken, preferences.pageSize, searchQuery]);
  
  useEffect(() => {
    if (geoLabelingJobConfigurationSummaryIds.length > 0) {
      setSearchTriggered(true);
    }
  }, [geoLabelingJobConfigurationSummaryIds]);
  */

  const loadJobs = useCallback(async () => {
    setLoading(true);
    const { campaigns: newCampaigns, LastEvaluatedKey } = await listCampaigns(
      preferences.pageSize,
      nextToken
    );
    setCampaigns([...campaigns, ...newCampaigns]);
    setPageCount(LastEvaluatedKey ? currentPageIndex + 1 : currentPageIndex);
    if (LastEvaluatedKey) {
      setHasNextPage(true);
    } else {
      setHasNextPage(false);
    }
    setNextToken(LastEvaluatedKey);
    setLoading(false);
  }, [nextToken, currentPageIndex, campaigns, preferences.pageSize]);

  useEffect(() => {
    if (searchTriggered && isAuthenticated) {
      loadJobs();
      setSearchTriggered(false);
    }
  }, [searchTriggered, loadJobs, isAuthenticated]);

  const searchWithFilter = (filterText: string) => {
    setSearchQuery(filterText);
    setCurrentPageIndex(1);
    setSearchTriggered(true);
  };

  const clearFilter = () => {
    setSearchQuery("");
    setCurrentPageIndex(1);
    setSearchTriggered(true);
  };

  const fetchPage = (pageNumber: number) => {
    if (pageNumber === currentPageIndex) {
      return;
    }

    setCurrentPageIndex(pageNumber);
    if (pageNumber === pageCount && hasNextPage) {
      setSearchTriggered(true);
    }
  };

  const deleteSelectedJobs = async () => {
    const request = {
      JobIDs: selectedCampaigns.map((job) => job.JobName),
    };

    const response = deleteJobs(request);
    if (response.status === "ok") {
      setCurrentPageIndex(1);
      toggleDeleteModal();
    }
  };

  const toggleDeleteModal = () => {
    const currentModalVisibility = showDeleteModal;
    setShowDeleteModal(!currentModalVisibility);
  };

  return (
    <ContentContainer>
      <Table
        columnDefinitions={LIST_JOBS_COLUMN_DEFINITIONS}
        visibleColumns={preferences.visibleContent}
        items={campaigns.slice(
          preferences.pageSize * (currentPageIndex - 1),
          preferences.pageSize * currentPageIndex
        )}
        stickyHeader={true}
        resizableColumns={true}
        loading={loading}
        loadingText="Loading jobs"
        filter={
          <TextFilter
            filteringText={searchQuery}
            filteringPlaceholder="Find jobs"
            onChange={({ detail }) => searchWithFilter(detail.filteringText)}
          />
        }
        wrapLines={preferences.wrapLines}
        header={
          <TableHeader
            title="Geospatial Data Labeling Campaigns"
            deleteDisabled={selectedCampaigns.length === 0}
            deleteCallback={toggleDeleteModal}
          />
        }
        empty={<TableEmptyState onClearFilter={clearFilter} />}
        selectionType="multi"
        selectedItems={selectedCampaigns}
        onSelectionChange={({ detail }) =>
          setSelectedCampaigns(detail.selectedItems)
        }
        pagination={
          <Pagination
            ariaLabels={{
              nextPageLabel: "Next page",
              previousPageLabel: "Previous page",
              pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
            }}
            openEnd={hasNextPage}
            currentPageIndex={currentPageIndex}
            onChange={({ detail }) => fetchPage(detail.currentPageIndex)}
            pagesCount={pageCount}
          />
        }
        preferences={
          <Preferences
            preferences={preferences}
            setPreferences={setPreferences}
          />
        }
      ></Table>
      <DeleteModal
        onConfirm={deleteSelectedJobs}
        onCancel={toggleDeleteModal}
        selectedJobs={selectedCampaigns}
        isVisible={showDeleteModal}
      />
    </ContentContainer>
  );
};

export default ListCampaigns;
